"use client";

import { toast } from "sonner";
import { runReplay, enqueuePdfReplay, createAiReplayTurn, applyAiReplay, type AiReplayTurn } from "@/lib/pdf-replay";
import { exportStaticPdf, hasXfaMarker } from "@/lib/pdf-export";
import { pdfFirstUse, type PdfEntrySource } from "@/lib/pdf-first-use";
import { importPdfFile } from "@/lib/pdf-import";
import { createPdfFormEditBarrier, PdfFormEditError } from "@/lib/pdf-form-edit-barrier";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePdfiumEngine } from "@embedpdf/engines/react";
import type { PluginRegistry } from "@embedpdf/core";
import type {
  AnnotationPlugin,
  AnnotationScope,
  AnnotationTransferItem,
} from "@embedpdf/plugin-annotation";
import type { FormPlugin, FormScope } from "@embedpdf/plugin-form";
import type {
  DocumentManagerCapability,
  DocumentManagerPlugin,
} from "@embedpdf/plugin-document-manager";
import type { HistoryPlugin, HistoryScope } from "@embedpdf/plugin-history";
import type { UIPlugin } from "@embedpdf/plugin-ui";
import type {
  PdfEngine,
  PdfDocumentObject,
} from "@embedpdf/models";
import { PdfAnnotationSubtype } from "@embedpdf/models";
import {
  executePdfCommand,
  type RemotePdfCommand,
} from "@/lib/relay/pdf-executor";
import {
  deleteCheckpointsByChat,
  deleteCheckpointsByTab,
  deleteConversation,
  deleteTab,
  getCheckpoint,
  getDocument,
  listCheckpointsByChat,
  listTabs,
  putCheckpoint,
  putDocState,
  putTabWithDocument,
  type CheckpointRecord,
  type PdfDocState,
  type TabRecord,
} from "@/lib/pdf-storage";

export const MAX_TABS = 5;
const AUTOSAVE_DEBOUNCE_MS = 500;

export interface PdfTab {
  id: string;
  fileName: string;
  order: number;
}

/**
 * Per-tab runtime state. Everything here is derived from the viewer's live
 * plugin state — there is no parallel mirror. Autosave and checkpoint
 * capture snapshot the plugins on demand via `exportAnnotations()` +
 * `getFormValues()`, so the on-disk `docState` and the UI cannot diverge.
 */
interface LoadedTab {
  id: string;
  annotation: AnnotationScope;
  form: FormScope;
  history: HistoryScope;
  fileName: string;
  saveTimerId: ReturnType<typeof setTimeout> | null;
  unsubAnnotation: () => void;
  unsubForm: () => void;
  /**
   * Raised while we're pushing a persisted PdfDocState into the viewer
   * (initial hydrate, checkpoint restore). During this window, annotation
   * and form events are programmatic echoes of the import, not user intent.
   */
  isReplaying: boolean;
  /**
   * Incremented at the start of every replay. A replay's finalizer (flip
   * `isReplaying` back to false, purge history) captures its generation
   * and bails if a newer replay has superseded it — guards against a quick
   * double-restore where the first replay's finalizer would otherwise
   * stomp on the second one's in-flight state.
   */
  replayGen: number;
  replayQueue: Promise<void>;
  remoteRevision: number;
  /**
   * Set true when plugin-annotation emits `'loaded'` for this doc — i.e.
   * its `isInitialLoadComplete` is true and `importAnnotations` will
   * process synchronously instead of queuing. Replay waits on this before
   * calling `importAnnotations`; otherwise queued `create` events could
   * fire outside the replay window and get autosaved as user edits.
   */
  annotationReady: boolean;
  /**
   * Resolvers to wake up pending `waitAnnotationReady()` callers the moment
   * the `'loaded'` event fires. Set up before `openDocumentBuffer` is
   * called, so the event is never missed even if it arrives before anyone
   * awaits it.
   */
  annotationReadyWaiters: Array<() => void>;
}

interface PdfTabsContextValue {
  tabs: PdfTab[];
  activeTabId: string | null;
  engineReady: boolean;
  engineError: Error | null;
  isHydrating: boolean;
  /** True whenever the active tab's document isn't mounted in the viewer yet. */
  isActiveTabLoading: boolean;
  isDownloading: boolean;
  isImporting: boolean;
  addTab: (file: File, source?: PdfEntrySource, entry?: string) => Promise<void>;
  closeTab: (id: string) => Promise<void>;
  setActiveTab: (id: string) => void;
  undoActive: () => void;
  redoActive: () => void;
  downloadActive: (format?: "editable" | "static") => Promise<void>;
  /** Called by PdfEditor once EmbedPDF hands back the registry. */
  registerRegistry: (registry: PluginRegistry) => void;
  /**
   * Return the active tab's PDF as a File, sourced from the IDB-backed
   * document store we already maintain for hydration. Consumers outside
   * the provider (e.g. the PDF-assistant chat) use this to upload the
   * current document to the backend without reaching into IDB themselves.
   */
  getMainFile: (tabId: string) => Promise<File | null>;
  /**
   * Snapshot the active tab's current state and persist it as a checkpoint.
   * Called at conversation boundaries: baseline (messageId=null) on first
   * chat, before each user message, after each assistant turn. Idempotent —
   * overwriting an existing id is a no-op when the state matches.
   */
  captureCheckpoint: (
    tabId: string,
    chatId: string,
    messageId: string | null,
  ) => Promise<void>;
  /** Remove all checkpoints for a (tabId, chatId) — used on "New chat". */
  deleteCheckpointsForChat: (tabId: string, chatId: string) => Promise<void>;
  /**
   * Roll the active tab's PDF state back to the snapshot captured against
   * `(chatId, messageId)`. Deletes every non-widget annotation, re-imports
   * the checkpoint's annotations, and replays form values. Returns true if
   * a checkpoint existed and was applied, false otherwise.
   */
  restoreCheckpoint: (
    tabId: string,
    chatId: string,
    messageId: string | null,
  ) => Promise<boolean>;
  /**
   * List checkpoints for a chat. UI uses this to decide which assistant
   * messages in the transcript have a restorable snapshot attached.
   */
  listCheckpointsForChat: (
    tabId: string,
    chatId: string,
  ) => Promise<CheckpointRecord[]>;
  /**
   * Snapshot the active tab's current viewer state. Used by the chat to
   * ship the latest `PdfDocState` to the backend with each user message.
   * Returns null if the tab isn't loaded.
   */
  getDocState: (tabId: string) => Promise<PdfDocState | null>;
  /**
   * Replace the active tab's viewer state with the one emitted by the
   * backend (typically as part of a tool-result). Runs the same replay
   * path as `restoreCheckpoint`, preserving native undo history with autosave cancelled,
   * `wipeFirst` so the new state fully owns the doc. Persists after
   * replay so reload sees the same state. Returns false if the tab
   * isn't loaded yet.
   */
  applyDocState: (tabId: string, state: PdfDocState) => Promise<boolean>;
  /**
   * Record the doc state shipped to the backend at the start of an AI turn.
   * applyDocState uses it to preserve edits the user makes while the turn
   * streams. Call right before sending a user message; pair with endAiTurn
   * when the turn settles.
   */
  beginAiTurn: (tabId: string, base: PdfDocState) => void;
  endAiTurn: (tabId: string) => Promise<void>;
  executeRemotePdf: (
    tabId: string,
    command: RemotePdfCommand,
  ) => Promise<Record<string, unknown>>;
}

const PdfTabsContext = createContext<PdfTabsContextValue | null>(null);

export function usePdfTabs(): PdfTabsContextValue {
  const ctx = useContext(PdfTabsContext);
  if (!ctx) throw new Error("usePdfTabs must be used inside PdfTabsProvider");
  return ctx;
}

function makeUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function toPdfTab(record: TabRecord): PdfTab {
  return { id: record.id, fileName: record.fileName, order: record.order };
}

/**
 * Snapshot the current viewer state as a canonical PdfDocState. `annotations`
 * excludes widget annotations — those belong to the PDF's AcroForm and
 * round-trip through `formValues` / re-parsing the base PDF on next open.
 */
/**
 * Rewrite stamp contexts from the appearance export to a flat PNG of the
 * annotation's unrotated artwork.
 *
 * exportAnnotations() hands back a PDF appearance stream for stamps. Feeding
 * that appearance back through createPageAnnotation applies the rotation
 * placement a second time — the artwork lands outside the annotation box and
 * a reloaded rotated image shows up clipped or blank (verified against the
 * raw engine with every rect/rotation permutation; only fresh creation from
 * image bytes renders correctly). A PNG ctx makes every import take the
 * fresh-creation path on both runtimes.
 */
async function normalizeStampContexts(
  items: AnnotationTransferItem[],
  engine: PdfEngine<Blob> | null,
  doc: PdfDocumentObject | null,
): Promise<AnnotationTransferItem[]> {
  if (!engine || !doc) return items;
  const out: AnnotationTransferItem[] = [];
  for (const item of items) {
    if (item.annotation.type !== PdfAnnotationSubtype.STAMP) {
      out.push(item);
      continue;
    }
    try {
      const page = doc.pages[item.annotation.pageIndex];
      const blob = await engine
        .renderPageAnnotation(doc, page, item.annotation, {
          unrotated: true,
          scaleFactor: 3,
          imageType: "image/png",
        })
        .toPromise();
      const data = await blob.arrayBuffer();
      out.push({ ...item, ctx: { data, mimeType: "image/png" } });
    } catch (err) {
      console.warn(
        "[fillapp] stamp ctx normalization failed, keeping appearance:",
        err,
      );
      out.push(item);
    }
  }
  return out;
}

async function snapshotDocState(
  annotation: AnnotationScope,
  form: FormScope,
  engine: PdfEngine<Blob> | null = null,
  doc: PdfDocumentObject | null = null,
): Promise<PdfDocState> {
  const annotations = await annotation
    .exportAnnotations()
    .toPromise()
    .then((items) =>
      items.filter(
        (item) => item.annotation.type !== PdfAnnotationSubtype.WIDGET,
      ),
    )
    .then((items) => normalizeStampContexts(items, engine, doc));
  return { annotations, formValues: form.getFormValues() };
}

/**
 * Serialize the live viewer doc to PDF bytes. Widget fields carry filled
 * values in their appearance streams. Overlays remain interactive.
 * Original XFA data is retained, so dynamic XFA viewers can prefer the
 * original values; static export is available when fixed visible values
 * are required across readers.
 */
async function generateDownload(
  engine: PdfEngine<Blob>,
  doc: PdfDocumentObject,
): Promise<Uint8Array> {
  const bytes = await engine.saveAsCopy(doc).toPromise();
  return new Uint8Array(bytes);
}

interface PdfTabsProviderProps {
  children: React.ReactNode;
}

export function PdfTabsProvider({ children }: PdfTabsProviderProps) {
  const { engine, error: engineError } = usePdfiumEngine();

  const [tabs, setTabs] = useState<PdfTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isHydrating, setIsHydrating] = useState(true);
  const [loadedTabIds, setLoadedTabIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importingRef = useRef(false);
  const [registryReady, setRegistryReady] = useState(false);

  // Registry + loaded tabs live in refs: updates to them don't need to
  // re-render the provider tree. The React state above mirrors whatever the
  // consumer actually needs to see.
  const registryRef = useRef<PluginRegistry | null>(null);
  const formEditBarrierRef = useRef<ReturnType<typeof createPdfFormEditBarrier> | null>(null);
  const loadedTabsRef = useRef<Map<string, LoadedTab>>(new Map());
  // Tabs whose load is in-flight — guards against concurrent hydrations for
  // the same id (e.g. when addTab races with the hydration effect).
  const pendingLoadsRef = useRef<Set<string>>(new Set());
  // Mirror of the same value for callbacks that can't re-close over state
  // every render. Always updated *with* setActiveTabId via setActive().
  const activeTabIdRef = useRef<string | null>(null);
  // The doc state each tab shipped to the backend at the start of the
  // current AI turn. Used by applyDocState to 3-way-merge the AI's result
  // with edits the user made concurrently (parallel editing) — see
  // beginAiTurn / applyDocState.
  const remoteBusyRef = useRef(new Set<string>());
  const aiTurnBaseRef = useRef<Map<string, AiReplayTurn>>(new Map());

  const setActive = useCallback((id: string | null) => {
    activeTabIdRef.current = id;
    setActiveTabId(id);
  }, []);

  const getDocManager = useCallback((): DocumentManagerCapability | null => {
    const registry = registryRef.current;
    if (!registry) return null;
    return (
      registry
        .getPlugin<DocumentManagerPlugin>("document-manager")
        ?.provides() ?? null
    );
  }, []);

  /**
   * Resolve the live engine + doc for a tab so snapshots can re-render
   * stamp artwork (see normalizeStampContexts). Both live in the viewer's
   * registry — same reasoning as downloadActive.
   */
  const getEngineAndDoc = useCallback(
    (
      tabId: string,
    ): { engine: PdfEngine<Blob> | null; doc: PdfDocumentObject | null } => {
      const registry = registryRef.current;
      const docManager = getDocManager();
      return {
        engine: (registry?.getEngine() as PdfEngine<Blob> | undefined) ?? null,
        doc: docManager?.getDocument(tabId) ?? null,
      };
    },
    [getDocManager],
  );

  const persistDocState = useCallback(
    async (tabId: string) => {
      const loaded = loadedTabsRef.current.get(tabId);
      if (!loaded) return;
      try {
        const { engine: snapEngine, doc } = getEngineAndDoc(tabId);
        const state = await snapshotDocState(
          loaded.annotation,
          loaded.form,
          snapEngine,
          doc,
        );
        await putDocState(tabId, state);
      } catch (err) {
        console.warn("[fillapp] persistDocState failed:", err);
      }
    },
    [getEngineAndDoc],
  );

  const scheduleSave = useCallback(
    (tabId: string) => {
      const loaded = loadedTabsRef.current.get(tabId);
      if (!loaded) return;
      if (loaded.saveTimerId) clearTimeout(loaded.saveTimerId);
      loaded.saveTimerId = setTimeout(() => {
        loaded.saveTimerId = null;
        void persistDocState(tabId);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [persistDocState],
  );

  /**
   * Bring one tab fully online: open in EmbedPDF's doc manager, wire event
   * subscriptions for autosave, replay any persisted state. Safe to call
   * multiple times for the same id — dedup'd via pendingLoadsRef and
   * loadedTabsRef.
   */
  const hydrateTab = useCallback(
    async (tab: PdfTab) => {
      if (!engine) return;
      const registry = registryRef.current;
      if (!registry) return;
      if (loadedTabsRef.current.has(tab.id)) return;
      if (pendingLoadsRef.current.has(tab.id)) return;
      pendingLoadsRef.current.add(tab.id);

      try {
        const doc = await getDocument(tab.id);
        if (!doc) return;

        const docManager = registry
          .getPlugin<DocumentManagerPlugin>("document-manager")
          ?.provides();
        const annotationCap = registry
          .getPlugin<AnnotationPlugin>("annotation")
          ?.provides();
        const formCap = registry.getPlugin<FormPlugin>("form")?.provides();
        const historyCap = registry
          .getPlugin<HistoryPlugin>("history")
          ?.provides();
        if (!docManager || !annotationCap || !formCap || !historyCap) return;

        // Scopes are bound to a documentId and safe to build before the doc
        // is open — `forDocument` just closes over the id. We wire event
        // subscribers *before* `openDocumentBuffer` so we can't miss the
        // plugin-annotation `'loaded'` emission: `events$` doesn't cache,
        // and the emission happens whenever `getAllAnnotations` resolves,
        // which can be before or after `openResp.task` awaits complete.
        const annotation = annotationCap.forDocument(tab.id);
        const form = formCap.forDocument(tab.id);
        const history = historyCap.forDocument(tab.id);

        const loaded: LoadedTab = {
          id: tab.id,
          annotation,
          form,
          history,
          fileName: tab.fileName,
          saveTimerId: null,
          unsubAnnotation: () => {},
          unsubForm: () => {},
          isReplaying: false,
          replayGen: 0,
          replayQueue: Promise.resolve(),
          remoteRevision: 0,
          annotationReady: false,
          annotationReadyWaiters: [],
        };

        // Autosave: every committed non-replay event triggers a debounced
        // snapshot-and-write. The `'loaded'` event flips `annotationReady`
        // so pending `runReplay` calls know when `importAnnotations` will
        // process synchronously. We filter pre-commit events (in-flight
        // drag/resize/typing) to avoid churning IDB on every mouse move.
        loaded.unsubAnnotation = annotation.onAnnotationEvent((event) => {
          if (event.type === "loaded") {
            if (!loaded.annotationReady) {
              loaded.annotationReady = true;
              const waiters = loaded.annotationReadyWaiters.splice(0);
              for (const w of waiters) w();
            }
            return;
          }
          loaded.remoteRevision += 1;
          if (loaded.isReplaying) return;
          if ("committed" in event && event.committed === false) return;
          if (loaded.annotationReady) pdfFirstUse.edited(tab.id);
          scheduleSave(tab.id);
        });
        loaded.unsubForm = form.onFieldValueChange(() => {
          loaded.remoteRevision += 1;
          if (loaded.isReplaying) return;
          if (loaded.annotationReady) pdfFirstUse.edited(tab.id);
          scheduleSave(tab.id);
        });

        loadedTabsRef.current.set(tab.id, loaded);

        // EmbedPDF wants its own ArrayBuffer — slice so we don't share the
        // backing store the caller may still hold.
        const buffer = doc.originalBytes.buffer.slice(
          doc.originalBytes.byteOffset,
          doc.originalBytes.byteOffset + doc.originalBytes.byteLength,
        ) as ArrayBuffer;

        const openTask = docManager.openDocumentBuffer({
          documentId: tab.id,
          buffer,
          name: tab.fileName,
          autoActivate: tab.id === activeTabIdRef.current,
        });
        const openResp = await openTask.toPromise();
        await openResp.task.toPromise();

        // Surface the thumbnails sidebar for this doc. `defaultOpen` on the
        // sidebar schema is ignored by the plugin's reducer — sidebars only
        // render when setActiveSidebar has populated the doc's state.
        const uiCap = registry.getPlugin<UIPlugin>("ui")?.provides();
        if (window.matchMedia("(min-width: 768px)").matches)
          uiCap?.setActiveSidebar("left", "main", "sidebar-panel", tab.id);

        if (doc.docState) {
          try {
            await enqueuePdfReplay(loaded, () => runReplay(loaded, doc.docState!, { wipeFirst: false }));
          } catch (err) {
            console.warn("[fillapp] hydrate replay failed:", err);
          }
        }

        setLoadedTabIds((prev) => {
          if (prev.has(tab.id)) return prev;
          const next = new Set(prev);
          next.add(tab.id);
          return next;
        });
      } catch (err) {
        console.error("[fillapp] hydrateTab failed:", err);
      } finally {
        pendingLoadsRef.current.delete(tab.id);
      }
    },
    [engine, scheduleSave],
  );

  // Initial tab-list load from IDB. Runs once on mount. The provider lives
  // in `(tools)/layout.tsx`, so this fires once per session regardless of
  // which `/(tools)/*` route the user lands on — every slug page is just an
  // SEO facade over the same editor instance.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const records = await listTabs();
        if (cancelled) return;
        const list = records.map(toPdfTab);
        setTabs(list);
        const requestedDocument = new URLSearchParams(window.location.search).get("document");
        if (requestedDocument) {
          const matchingTab = list.find((tab) => tab.id === requestedDocument);
          if (matchingTab) setActive(matchingTab.id);
          else toast.info("This PDF is saved in the browser where you shared it.");
        } else if (list.length > 0) setActive(list[0].id);
      } catch (err) {
        console.error("[fillapp] failed to load tabs:", err);
      } finally {
        if (!cancelled) setIsHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActive]);

  // Hydrate any tab that's in React state but not yet loaded into the
  // viewer. Runs after `registerRegistry` + whenever tabs change.
  useEffect(() => {
    if (!registryReady || !engine) return;
    for (const tab of tabs) {
      if (!loadedTabsRef.current.has(tab.id)) {
        void hydrateTab(tab);
      }
    }
  }, [registryReady, engine, tabs, hydrateTab]);

  const registerRegistry = useCallback((registry: PluginRegistry) => {
    if (registryRef.current === registry) return;
    const form = registry.getPlugin<FormPlugin>("form");
    if (form) {
      const barrier = createPdfFormEditBarrier(form.provides());
      // Adapt the public capability before any document widgets are mounted.
      form.provides = () => barrier.capability;
      formEditBarrierRef.current = barrier;
    }
    registryRef.current = registry;
    setRegistryReady(true);
  }, []);

  const addTab = useCallback(
    async (file: File, source: PdfEntrySource = "upload", entry = "editor") => {
      if (importingRef.current) return;
      if (tabs.length >= MAX_TABS) {
        toast.error(`You can open up to ${MAX_TABS} PDFs at once`, {
          description: "Close a tab to open another.",
        });
        return;
      }
      importingRef.current = true;
      setIsImporting(true);
      try {
        let imported: Awaited<ReturnType<typeof importPdfFile>>;
        try {
          imported = await importPdfFile(file, engine);
        } catch (error) {
          toast.error(error instanceof Error ? error.message : "Couldn't open this file. Please try again.");
          return;
        }
        const id = makeUuid();
        const now = Date.now();
        const order =
          tabs.length === 0 ? 0 : Math.max(...tabs.map((t) => t.order)) + 1;
        const record: TabRecord = {
          id,
          fileName: imported.fileName,
          order,
          createdAt: now,
          updatedAt: now,
        };
        try {
          await putTabWithDocument(record, imported.bytes);
        } catch (err) {
          console.error("[fillapp] failed to store document:", err);
          toast.error("Couldn't open this PDF", {
            description:
              "Your browser storage may be full. Free up space and try again.",
          });
          return;
        }
        setTabs((prev) => [...prev, toPdfTab(record)]);
        pdfFirstUse.opened(id, source, entry, imported.inputFormat);
        setActive(id);
        // Hydration will fire from the effect above once React flushes.
      } finally {
        importingRef.current = false;
        setIsImporting(false);
      }
    },
    [tabs, setActive, engine],
  );

  const closeTab = useCallback(
    async (id: string) => {
      const loaded = loadedTabsRef.current.get(id);
      if (loaded) {
        if (loaded.saveTimerId) clearTimeout(loaded.saveTimerId);
        loaded.unsubAnnotation();
        loaded.unsubForm();
        loadedTabsRef.current.delete(id);
      }
      setLoadedTabIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const docManager = getDocManager();
      if (docManager) {
        try {
          await docManager.closeDocument(id).toPromise();
        } catch (err) {
          console.warn("[fillapp] closeDocument failed:", err);
        }
      }
      await deleteTab(id);
      formEditBarrierRef.current?.forget(id);
      pdfFirstUse.closed(id);
      await deleteConversation(id);
      await deleteCheckpointsByTab(id).catch((err) =>
        console.warn("[fillapp] deleteCheckpointsByTab failed:", err),
      );
      setTabs((prev) => {
        const next = prev.filter((t) => t.id !== id);
        if (activeTabIdRef.current === id) {
          setActive(next[0]?.id ?? null);
        }
        return next;
      });
    },
    [getDocManager, setActive],
  );

  const setActiveTab = useCallback(
    (id: string) => {
      if (id === activeTabIdRef.current) return;
      const docManager = getDocManager();
      docManager?.setActiveDocument(id);
      setActive(id);
    },
    [getDocManager, setActive],
  );

  const undoActive = useCallback(() => {
    const active = activeTabIdRef.current;
    if (!active) return;
    const loaded = loadedTabsRef.current.get(active);
    loaded?.history.undo();
  }, []);

  const redoActive = useCallback(() => {
    const active = activeTabIdRef.current;
    if (!active) return;
    const loaded = loadedTabsRef.current.get(active);
    loaded?.history.redo();
  }, []);

  const getMainFile = useCallback(
    async (tabId: string): Promise<File | null> => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return null;
      const doc = await getDocument(tabId);
      if (!doc) return null;
      const copy = new Uint8Array(doc.originalBytes);
      return new File([copy], tab.fileName, { type: "application/pdf" });
    },
    [tabs],
  );

  const captureCheckpoint = useCallback(
    async (tabId: string, chatId: string, messageId: string | null) => {
      const loaded = loadedTabsRef.current.get(tabId);
      if (!loaded) return;
      try {
        if (await getCheckpoint(tabId, chatId, messageId)) return;
        await loaded.replayQueue;
        // Flush any pending autosave first so the checkpoint and the
        // IDB-persisted docState always agree.
        if (loaded.saveTimerId) {
          clearTimeout(loaded.saveTimerId);
          loaded.saveTimerId = null;
          await persistDocState(tabId);
        }
        const { engine: cpEngine, doc: cpDoc } = getEngineAndDoc(tabId);
        const state = await snapshotDocState(
          loaded.annotation,
          loaded.form,
          cpEngine,
          cpDoc,
        );
        // A restore point is immutable, including when a pending message is
        // hydrated again after the document has already changed.
        await putCheckpoint(tabId, chatId, messageId, state);
      } catch (err) {
        console.warn("[fillapp] captureCheckpoint failed:", err);
      }
    },
    [persistDocState],
  );

  const deleteCheckpointsForChat = useCallback(
    async (tabId: string, chatId: string) => {
      try {
        await deleteCheckpointsByChat(tabId, chatId);
      } catch (err) {
        console.warn("[fillapp] deleteCheckpointsForChat failed:", err);
      }
    },
    [],
  );

  const listCheckpointsForChat = useCallback(
    async (tabId: string, chatId: string) => {
      try {
        return await listCheckpointsByChat(tabId, chatId);
      } catch (err) {
        console.warn("[fillapp] listCheckpointsForChat failed:", err);
        return [];
      }
    },
    [],
  );

  const restoreCheckpoint = useCallback(
    async (
      tabId: string,
      chatId: string,
      messageId: string | null,
    ): Promise<boolean> => {
      const loaded = loadedTabsRef.current.get(tabId);
      if (!loaded) return false;
      const record = await getCheckpoint(tabId, chatId, messageId);
      if (!record) return false;

      try {
        await enqueuePdfReplay(loaded, () => runReplay(loaded, record.docState, { wipeFirst: true }));
      } catch (err) {
        console.warn("[fillapp] restoreCheckpoint failed:", err);
        return false;
      }

      // Persist the restored state so a reload (or the next autosave tick)
      // sees the same docState the UI now shows.
      await persistDocState(tabId);
      return true;
    },
    [persistDocState],
  );

  const getDocState = useCallback(
    async (tabId: string): Promise<PdfDocState | null> => {
      const loaded = loadedTabsRef.current.get(tabId);
      if (!loaded) return null;
      try {
        await loaded.replayQueue;
        const { engine: gdEngine, doc: gdDoc } = getEngineAndDoc(tabId);
        return await snapshotDocState(
          loaded.annotation,
          loaded.form,
          gdEngine,
          gdDoc,
        );
      } catch (err) {
        console.warn("[fillapp] getDocState failed:", err);
        return null;
      }
    },
    [getEngineAndDoc],
  );

  const beginAiTurn = useCallback((tabId: string, base: PdfDocState) => {
    if (remoteBusyRef.current.has(tabId))
      throw new Error(
        "Your connected AI is working on this PDF. Wait for it to finish, then retry.",
      );
    aiTurnBaseRef.current.set(tabId, createAiReplayTurn(base, loadedTabsRef.current.get(tabId)));
  }, []);

  const endAiTurn = useCallback(async (tabId: string) => {
    const turn = aiTurnBaseRef.current.get(tabId);
    await loadedTabsRef.current.get(tabId)?.replayQueue;
    if (aiTurnBaseRef.current.get(tabId) === turn) aiTurnBaseRef.current.delete(tabId);
  }, []);

  const applyDocState = useCallback(
    async (tabId: string, state: PdfDocState): Promise<boolean> => {
      const loaded = loadedTabsRef.current.get(tabId);
      if (!loaded) return false;
      const turn = aiTurnBaseRef.current.get(tabId);
      return enqueuePdfReplay(loaded, async () => {
        try {
          await applyAiReplay(loaded, state, turn);
          await persistDocState(tabId);
          return true;
        } catch (err) {
          console.warn("[fillapp] applyDocState failed:", err);
          return false;
        }
      });
    },
    [persistDocState],
  );

  const executeRemotePdf = useCallback(
    async (tabId: string, command: RemotePdfCommand) => {
      const loaded = loadedTabsRef.current.get(tabId);
      const { engine: liveEngine, doc } = getEngineAndDoc(tabId);
      if (
        !loaded ||
        !doc ||
        !liveEngine ||
        loaded.isReplaying ||
        !loaded.annotationReady
      ) {
        throw new Error(
          "Document is not ready. Open it in the editor and try again.",
        );
      }
      if (aiTurnBaseRef.current.has(tabId))
        throw new Error(
          "FillApp chat is editing this document. Wait for it to finish.",
        );
      if (remoteBusyRef.current.has(tabId))
        throw new Error(
          "Another PDF operation is running. Retry after it finishes.",
        );
      remoteBusyRef.current.add(tabId);
      try {
        return await executePdfCommand(
          {
            documentId: tabId,
            hasXfa: hasXfaMarker((await getDocument(tabId))?.originalBytes ?? new Uint8Array()),
            fileName: loaded.fileName,
            editorUrl: `${window.location.origin}${window.location.pathname}?document=${encodeURIComponent(tabId)}`,
            revision: () => loaded.remoteRevision,
            engine: liveEngine,
            doc,
            form: loaded.form,
            annotation: loaded.annotation,
            persist: () => persistDocState(tabId),
            save: () => generateDownload(liveEngine, doc),
          },
          command,
        );
      } finally {
        remoteBusyRef.current.delete(tabId);
      }
    },
    [getEngineAndDoc, persistDocState],
  );

  const downloadActive = useCallback(async (format?: "editable" | "static") => {
    const active = activeTabIdRef.current;
    if (!active) return;
    const loaded = loadedTabsRef.current.get(active);
    if (!loaded) return;
    const registry = registryRef.current;
    const docManager = getDocManager();
    const doc = docManager?.getDocument(active);
    // The viewer's `<PDFViewer>` creates its own engine + worker; documents
    // opened through `docManager` live in *that* engine's registry, not the
    // one exposed by `usePdfiumEngine()`. Always drive exports through
    // `registry.getEngine()` so we hit the same worker the doc is open in.
    const registryEngine = registry?.getEngine() as PdfEngine<Blob> | undefined;
    if (!doc || !registryEngine) return;
    setIsDownloading(true);
    try {
      await formEditBarrierRef.current?.wait(active);
      // Flush any pending autosave before generating so the downloaded PDF
      // reflects the same state the UI shows.
      if (loaded.saveTimerId) {
        clearTimeout(loaded.saveTimerId);
        loaded.saveTimerId = null;
        await persistDocState(active);
      }
      const exportFormat = format ?? "static";
      if (exportFormat === "editable") {
        toast.info("Editable copies may retain original dynamic form data. Choose Download PDF for a fixed copy.", { duration: 12000 });
      }
      await loaded.annotation.commit().toPromise();
      const bytes = exportFormat === "static" ? await exportStaticPdf(registryEngine, doc) : await generateDownload(registryEngine, doc);
      pdfFirstUse.exported(active, exportFormat, bytes);
      const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = loaded.fileName.replace(/\.pdf$/i, "") + (exportFormat === "static" ? "-static.pdf" : "-filled.pdf");
      a.click();
      URL.revokeObjectURL(url);
      if (exportFormat === "static") toast.info("Fixed copy. Continue editing in FillApp.");
    } catch (err) {
      console.error("[fillapp] download failed:", err);
      toast.error(err instanceof PdfFormEditError ? err.message : "PDF download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }, [getDocManager, persistDocState]);

  const isActiveTabLoading = !!activeTabId && !loadedTabIds.has(activeTabId);

  const value = useMemo<PdfTabsContextValue>(
    () => ({
      tabs,
      activeTabId,
      engineReady: !!engine,
      engineError,
      isHydrating,
      isActiveTabLoading,
      isDownloading,
      isImporting,
      addTab,
      closeTab,
      setActiveTab,
      undoActive,
      redoActive,
      downloadActive,
      registerRegistry,
      getMainFile,
      captureCheckpoint,
      deleteCheckpointsForChat,
      restoreCheckpoint,
      listCheckpointsForChat,
      getDocState,
      applyDocState,
      beginAiTurn,
      endAiTurn,
      executeRemotePdf,
    }),
    [
      tabs,
      activeTabId,
      engine,
      engineError,
      isHydrating,
      isActiveTabLoading,
      isDownloading,
      isImporting,
      addTab,
      closeTab,
      setActiveTab,
      undoActive,
      redoActive,
      downloadActive,
      registerRegistry,
      getMainFile,
      captureCheckpoint,
      deleteCheckpointsForChat,
      restoreCheckpoint,
      listCheckpointsForChat,
      getDocState,
      applyDocState,
      beginAiTurn,
      endAiTurn,
      executeRemotePdf,
    ],
  );

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || process.env.NEXT_PUBLIC_PDF_WORKSPACE_TEST_BRIDGE !== "true") return;
    const target = window as unknown as { __fillappPdfTest?: PdfTabsContextValue };
    target.__fillappPdfTest = value;
    return () => { delete target.__fillappPdfTest; };
  }, [value]);

  return (
    <PdfTabsContext.Provider value={value}>{children}</PdfTabsContext.Provider>
  );
}
